// useSanearFiltroSector.ts — el filtro de sector que se recordó puede apuntar a un sector que ya no existe.
//
// Round 2026-10-29 (pendiente 2.6 de retomar.md). `useFiltroPersistido` recuerda por usuario el filtro de /aseos,
// sector incluido, y lo guarda con el NOMBRE del sector. Si después el sector se renombra o se borra en
// Configuración → Zonas geográficas, el nombre recordado ya no figura en el catálogo: la tabla filtra por un
// sector que no existe, queda en cero y nada en la pantalla lo explica. Este hook compara la selección con el
// catálogo y, si hay nombres que ya no existen, los quita; si no queda ninguno, el filtro vuelve a «todos».
//
// Reglas, con su porqué:
//   · Sin catálogo todavía (llega por red, o falló) NO se toca nada: sin él no se sabe qué existe.
//   · Un enlace directo MANDA: si la página se abrió con `?sector=` (o `?aseador=`, `?desde=&hasta=`), nada se
//     restauró de lo guardado y el nombre que trae el enlace se respeta aunque no exista —el aviso de un sector
//     renombrado antes de esta corrección sigue llevando el nombre viejo, y quien lo toca debe ver exactamente
//     lo que pidió—. Eso es `omitir`. Los nombres que un enlace pone con la página ya abierta (tocar un aviso)
//     van en `protegidos`.
//   · El valor limpio se aplica con el setter que NO guarda (`useFiltroPersistido` devuelve un tercero): lo
//     guardado no se reescribe desde acá, porque quien decide qué recordar es la persona al tocar un filtro. En
//     cuanto toca cualquiera, se guarda el estado limpio.
//   · Una vez limpio no vuelve a actuar (ya no hay nombres que no existan), así que no hay bucle.
import { useEffect } from "react";

/**
 * De la selección, los nombres que SIGUEN existiendo, o `null` si no hay nada que cambiar.
 *
 * La comparación es exacta, mayúsculas incluidas (así filtra la tabla: `sector.includes(aseo.sector)`), de modo que
 * un sector que solo cambió de mayúsculas (`playa` → `Playa`) también cuenta como distinto.
 *
 * @param seleccionados nombres de sector del filtro
 * @param catalogo nombres de los sectores que existen hoy; vacío = aún no se sabe
 * @param protegidos nombres que no se quitan aunque no existan (los impuso un enlace)
 */
export function sectoresQueSiguenExistiendo(
  seleccionados: readonly string[],
  catalogo: readonly string[],
  protegidos?: ReadonlySet<string>,
): string[] | null {
  if (seleccionados.length === 0 || catalogo.length === 0) return null;
  const existentes = new Set(catalogo);
  const vigentes = seleccionados.filter((n) => existentes.has(n) || protegidos?.has(n));
  return vigentes.length === seleccionados.length ? null : vigentes;
}

type Opciones = {
  /** Nombres de los sectores que existen hoy (vacío mientras no carga). Debe ser estable entre renders. */
  catalogo: readonly string[];
  /** Nombres de sector del filtro actual. */
  seleccionados: readonly string[];
  /** Aplica la selección limpia (con el setter que NO guarda lo recordado). */
  aplicar: (vigentes: string[]) => void;
  /** `true` si la página se abrió con un enlace directo: no se limpia nada. */
  omitir?: boolean;
  /** Nombres que un enlace puso con la página ya abierta (se leen en cada pasada). */
  protegidos?: { current: ReadonlySet<string> };
};

export function useSanearFiltroSector({
  catalogo,
  seleccionados,
  aplicar,
  omitir = false,
  protegidos,
}: Opciones): void {
  useEffect(() => {
    if (omitir) return;
    const vigentes = sectoresQueSiguenExistiendo(seleccionados, catalogo, protegidos?.current);
    if (vigentes) aplicar(vigentes);
    // `aplicar` y `protegidos` cambian de identidad sin cambiar de sentido: depender de ellos relanzaría la pasada.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [omitir, catalogo, seleccionados]);
}
